import React, { useEffect, useState } from 'react';
import Screen from '../../components/common/Screen';
import Card from '../../components/elements/Card';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo, logout } from '../../utils';
import Icon from '../../components/common/Icon';
import useResetSubmittingOnRestore from '../../hooks/useResetSubmittingOnRestore';

// Icon + accent per selector card, keyed by the descriptor `key` the backend emits.
// Colour follows the business (Bravos ink, PLG pink, legacy per role-kind).
const KEY_META = {
  bravosCompany:   { color: '#002335', icon: 'user-crown' },
  bravosMember:    { color: '#002335', icon: 'trophy'     },
  bravosEcosystem: { color: '#002335', icon: 'trophy'     },
  plgCompany:      { color: '#e32574', icon: 'user-crown' },
  plgCommunity:    { color: '#e32574', icon: 'users'      },
  plgPlayer:       { color: '#e32574', icon: 'running'    },
  legacyContact:   { color: '#25D366', icon: 'user-crown' },
  legacyPlayer:    { color: '#FF784B', icon: 'running'    },
  crew:            { color: '#FF3B30', icon: 'megaphone'  },
  supporter:       { color: '#5AC8FA', icon: 'following'  },
  // Our own people, not a customer role — its own ink so it never reads as one
  // of the branded cards above. ONE card per business unit, whatever they were
  // granted on it: the CRM on the road and the invoice capture share a home, so
  // two cards would mean picking which half of it you wanted. 'sales' and
  // 'finance' stay mapped for a session minted before the two were merged.
  internal:        { color: '#0F766E', icon: 'marker'     },
  sales:           { color: '#0F766E', icon: 'marker'     },
  finance:         { color: '#0F766E', icon: 'marker'     },
};

const cardKey = (d) => `${d.key}:${d.bu_id || ''}:${d.company_id || d.team_id || ''}`;

const Page = () => {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    fetchUserInfo('/role', setUserInfo, setLoading, setError);
  }, []);

  // Unstick the `submitting` lock when the page is restored from the back/forward
  // cache, otherwise "Back" leaves every role card unclickable. See the hook.
  useResetSubmittingOnRestore(setSubmitting);

  const handleRoleSelect = async (d) => {
    if (submitting) return;
    const ck = cardKey(d);
    try {
      setSubmitting(ck);
      const response = await fetch('/api/role', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          credentials: 'include',
        },
        // The backend re-derives the person's cards and matches on these ids — it
        // never trusts them beyond confirming they belong to a real card.
        body: JSON.stringify({
          key: d.key, role: d.role, bu_id: d.bu_id,
          company_id: d.company_id, team_id: d.team_id,
        }),
      });
      if (!response.ok) throw new Error('Failed to select role');
      const result = await response.json().catch(() => ({}));
      // Keep the NavBar's cached role in sync with the JWT we just rewrote.
      // Otherwise the bottom bar keeps the old role's left icon until the session
      // is cleared.
      sessionStorage.setItem('role', d.role);
      // The backend hands back the destination for the chosen card.
      window.location.href = (result && result.redirect) || d.redirect || '/home';
    } catch (err) {
      console.error('Error selecting role:', err);
      setSubmitting(null);
    }
  };

  return (
    <Screen>
      <div className="top">
        <div className="role-shell">
          <div className="role-header">
            <h2 className="role-header__title">{t('selectYourRole')}</h2>
            <p className="role-header__sub">{t('selectYourRoleSub')}</p>
            <button
              className="role-header__logout"
              onClick={logout}
              aria-label={t('logout')}
            >
              <Icon name="sign-out-alt" theme="primary" />
            </button>
          </div>

          <div className="role-list">
            {userInfo?.descriptors?.map((d) => {
              const meta = KEY_META[d.key] || { color: '#888', icon: 'user' };
              const ck = cardKey(d);
              const isSubmitting = submitting === ck;
              return (
                <Card
                  key={ck}
                  mb="3"
                  mx="0"
                  p="0"
                  className={`role-card role-card--${d.key} ${isSubmitting ? 'role-card--loading' : ''}`}
                  onClick={() => handleRoleSelect(d)}
                  style={{ '--role-color': meta.color }}
                >
                  <div className="role-card__inner">
                    <div className="role-card__icon-wrap">
                      <Icon name={meta.icon} theme="light" />
                    </div>
                    <div className="role-card__body">
                      <div className="role-card__title">{t(d.label_key, { brand: d.bu_name || '' })}</div>
                      {/* Which company: somebody managing two companies holds a card for each. */}
                      {d.company_name && <div className="role-card__company">{d.company_name}</div>}
                      <div className="role-card__desc">{t(`${d.label_key}Desc`, { defaultValue: '', brand: d.bu_name || '' })}</div>
                    </div>
                    <Icon name="angle-right" theme="light" className="role-card__chevron" />
                  </div>
                </Card>
              );
            })}
          </div>

          {!loading && (!userInfo?.descriptors || userInfo.descriptors.length === 0) && (
            <div className="role-empty">{t('noRolesAvailable')}</div>
          )}
        </div>
      </div>
    </Screen>
  );
};

export default Page;
